/** IXD Preview Chunk 컴포넌트 */

import { memo } from "react";
import type { IxdChunk } from "@/apis/compare/route";
import { getUnitDivisor } from "@/utils/ixd-editor/unit-conversion";
import {
  getDlViewContentSchemaType,
  hasDlViewContent,
} from "@/utils/ixd-editor/content-display";
import {
  buildCellIdentityMapFromValues,
  cellChangeKey,
  type CellChangeOrigin,
} from "@/utils/ixd-cell-model";
import { BlockItemView } from "../block-item-view";
import { getContentEditInfo, type IxdDlView } from "./cell-lookup";
import {
  areStringMapsEqual,
  getChunkIdentityKeys,
  getChunkNormalizedCellIds,
  getDlViewIdentityKeys,
  getDlViewNormalizedCellIds,
  getRelevantChangeOrigins,
  getRelevantIdentityChanges,
  getRelevantRecordChanges,
} from "./chunk-change-detection";
import { DlView, DlViewSeparator } from "./dl-view";
import { useIxdPreviewConfig } from "./ixd-preview-config-context";

interface ChunkProps {
  chunk: IxdChunk;
  /** 동일 원본 chunk를 여러 renderer로 나눌 때 사용하는 DOM 전용 scope */
  renderScopeId?: string;
  /** 잘라낸 dlView 배열의 첫 항목이 원본에서 갖던 index */
  sourceDlViewIndexOffset?: number;
  isSelected: boolean;
  showPeriodLabels: boolean;
  hideBeforePreviousPeriod: boolean;
  cellChanges?: Record<string, string>;
  changeOrigins?: Record<string, CellChangeOrigin>;
  cellIdentityByNormalizedCellId?: Map<string, string>;
  identityChanges?: Map<string, string>;
  onCellEdit?: (
    cellId: string,
    newValue: string,
    identityKey?: string,
    locationUid?: string,
  ) => void;
}

interface ChunkDlViewItemProps {
  chunkId: string;
  renderScopeId?: string;
  sourceDlViewIndex: number;
  dlView: IxdDlView;
  index: number;
  isLast: boolean;
  hideBeforePreviousPeriod: boolean;
  showPeriodLabel: boolean;
  cellChanges?: Record<string, string>;
  changeOrigins?: Record<string, CellChangeOrigin>;
  cellIdentityByNormalizedCellId?: Map<string, string>;
  identityChanges?: Map<string, string>;
  onCellEdit?: (
    cellId: string,
    newValue: string,
    identityKey?: string,
    locationUid?: string,
  ) => void;
}

const areChunkDlViewItemPropsEqual = (
  previous: ChunkDlViewItemProps,
  next: ChunkDlViewItemProps,
): boolean => {
  if (previous.chunkId !== next.chunkId) return false;
  if (previous.renderScopeId !== next.renderScopeId) return false;
  if (previous.sourceDlViewIndex !== next.sourceDlViewIndex) return false;
  if (previous.dlView !== next.dlView) return false;
  if (previous.index !== next.index) return false;
  if (previous.isLast !== next.isLast) return false;
  if (previous.hideBeforePreviousPeriod !== next.hideBeforePreviousPeriod) {
    return false;
  }
  if (previous.showPeriodLabel !== next.showPeriodLabel) return false;
  if (
    previous.cellIdentityByNormalizedCellId !==
    next.cellIdentityByNormalizedCellId
  ) {
    return false;
  }
  if (previous.onCellEdit !== next.onCellEdit) return false;

  const normalizedCellIds = getDlViewNormalizedCellIds(next.dlView);
  const previousRecordChanges = getRelevantRecordChanges(
    previous.cellChanges,
    normalizedCellIds,
  );
  const nextRecordChanges = getRelevantRecordChanges(
    next.cellChanges,
    normalizedCellIds,
  );
  if (!areStringMapsEqual(previousRecordChanges, nextRecordChanges)) {
    return false;
  }

  const identityKeys = getDlViewIdentityKeys(
    next.dlView,
    next.cellIdentityByNormalizedCellId,
  );
  const previousIdentityChanges = getRelevantIdentityChanges(
    previous.identityChanges,
    identityKeys,
  );
  const nextIdentityChanges = getRelevantIdentityChanges(
    next.identityChanges,
    identityKeys,
  );

  if (!areStringMapsEqual(previousIdentityChanges, nextIdentityChanges)) {
    return false;
  }

  return areStringMapsEqual(
    getRelevantChangeOrigins(
      previous.changeOrigins,
      normalizedCellIds,
      identityKeys,
    ),
    getRelevantChangeOrigins(
      next.changeOrigins,
      normalizedCellIds,
      identityKeys,
    ),
  );
};

const ChunkDlViewItem = memo(function ChunkDlViewItem({
  chunkId,
  renderScopeId,
  sourceDlViewIndex,
  dlView,
  index,
  isLast,
  hideBeforePreviousPeriod,
  showPeriodLabel,
  cellChanges,
  changeOrigins,
  cellIdentityByNormalizedCellId,
  identityChanges,
  onCellEdit,
}: ChunkDlViewItemProps) {
  const sectionId = `${renderScopeId ?? chunkId}-dl-${index}`;
  const identitySectionId = `${chunkId}-dl-${sourceDlViewIndex}`;
  const {
    editable,
    showRawValueInEdit,
    onCellEditStart,
    onCellMouseDown,
    onCellMouseEnter,
    onCellMouseUp,
  } = useIxdPreviewConfig();

  if (hasDlViewContent(dlView)) {
    const contentEditInfo = getContentEditInfo(dlView);
    const dlViewIdentityByNormalizedCellId = buildCellIdentityMapFromValues(
      dlView.values,
    );
    const cellIdentityKey = contentEditInfo
      ? (dlViewIdentityByNormalizedCellId.get(
          contentEditInfo.normalizedCellId,
        ) ??
        cellIdentityByNormalizedCellId?.get(contentEditInfo.normalizedCellId))
      : undefined;
    const directPendingValue = contentEditInfo
      ? (cellChanges?.[cellChangeKey(contentEditInfo.cellId)] ??
        cellChanges?.[cellChangeKey(contentEditInfo.normalizedCellId)])
      : undefined;
    const identityPendingValue = cellIdentityKey
      ? identityChanges?.get(cellIdentityKey)
      : undefined;
    const pendingValue = identityPendingValue ?? directPendingValue;

    return (
      <div className="flex flex-col gap-10">
        <BlockItemView
          title={dlView.title}
          showTitle={dlView.titleUse !== false}
          content={dlView.content ?? ""}
          sectionId={sectionId}
          selectionId={`${sectionId}-content`}
          identityLocationUid={`${identitySectionId}-content`}
          schemaType={getDlViewContentSchemaType(dlView)}
          unitText={dlView.unitText}
          blockItemId={dlView.id}
          editable={editable}
          editedValue={pendingValue}
          editCellId={contentEditInfo?.cellId}
          cellIdentityKey={cellIdentityKey}
          onEdit={onCellEdit}
          onEditStart={onCellEditStart}
          showRawValueInEdit={showRawValueInEdit}
          onCellMouseDown={onCellMouseDown}
          onCellMouseEnter={onCellMouseEnter}
          onCellMouseUp={onCellMouseUp}
        />
        {!isLast && <DlViewSeparator />}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-10">
      <DlView
        dlView={{
          title: dlView.title,
          table: dlView.table,
          isTransposed: dlView.isTransposed,
          periodLabels: dlView.periodLabels,
          fsType: dlView.fsType,
          unitText: dlView.unitText,
          values: dlView.values,
          lineItemUnits: dlView.lineItemUnits,
        }}
        dlId={dlView.id}
        sectionId={sectionId}
        identitySectionId={identitySectionId}
        hideBeforePreviousPeriod={hideBeforePreviousPeriod}
        showPeriodLabel={showPeriodLabel}
        cellChanges={cellChanges}
        changeOrigins={changeOrigins}
        cellIdentityByNormalizedCellId={cellIdentityByNormalizedCellId}
        identityChanges={identityChanges}
        onCellEdit={onCellEdit}
        unitDivisor={getUnitDivisor(dlView.unitText)}
      />
      {!isLast && <DlViewSeparator />}
    </div>
  );
}, areChunkDlViewItemPropsEqual);

ChunkDlViewItem.displayName = "ChunkDlViewItem";

const areChunkPropsEqual = (
  previous: ChunkProps,
  next: ChunkProps,
): boolean => {
  if (previous.chunk !== next.chunk) return false;
  if (previous.renderScopeId !== next.renderScopeId) return false;
  if (previous.sourceDlViewIndexOffset !== next.sourceDlViewIndexOffset) {
    return false;
  }
  if (previous.isSelected !== next.isSelected) return false;
  if (previous.showPeriodLabels !== next.showPeriodLabels) return false;
  if (previous.hideBeforePreviousPeriod !== next.hideBeforePreviousPeriod) {
    return false;
  }
  if (
    previous.cellIdentityByNormalizedCellId !==
    next.cellIdentityByNormalizedCellId
  ) {
    return false;
  }
  if (previous.onCellEdit !== next.onCellEdit) return false;

  const normalizedCellIds = getChunkNormalizedCellIds(next.chunk);
  const previousRecordChanges = getRelevantRecordChanges(
    previous.cellChanges,
    normalizedCellIds,
  );
  const nextRecordChanges = getRelevantRecordChanges(
    next.cellChanges,
    normalizedCellIds,
  );
  if (!areStringMapsEqual(previousRecordChanges, nextRecordChanges)) {
    return false;
  }

  const identityKeys = getChunkIdentityKeys(
    next.chunk,
    next.cellIdentityByNormalizedCellId,
  );
  const previousIdentityChanges = getRelevantIdentityChanges(
    previous.identityChanges,
    identityKeys,
  );
  const nextIdentityChanges = getRelevantIdentityChanges(
    next.identityChanges,
    identityKeys,
  );

  if (!areStringMapsEqual(previousIdentityChanges, nextIdentityChanges)) {
    return false;
  }

  return areStringMapsEqual(
    getRelevantChangeOrigins(
      previous.changeOrigins,
      normalizedCellIds,
      identityKeys,
    ),
    getRelevantChangeOrigins(
      next.changeOrigins,
      normalizedCellIds,
      identityKeys,
    ),
  );
};

export const Chunk = memo(function Chunk({
  chunk,
  renderScopeId,
  sourceDlViewIndexOffset = 0,
  isSelected,
  showPeriodLabels,
  hideBeforePreviousPeriod,
  cellChanges,
  changeOrigins,
  cellIdentityByNormalizedCellId,
  identityChanges,
  onCellEdit,
}: ChunkProps) {
  // 사이드바 접기/펼치기는 content 폭을 500ms 동안 애니메이션해 매 프레임 relayout 을 돈다.
  // content-visibility 로 화면 밖 chunk 의 표는 레이아웃을 건너뛴다. intrinsic-size 의 auto 는
  // 한 번 렌더된 뒤 실측 높이를 기억해 목차 클릭(scrollIntoView) 위치가 어긋나지 않게 한다.
  return (
    <div
      className="bg-white [contain-intrinsic-size:auto_800px] [content-visibility:auto]"
      data-chunk-id={chunk.id}
      data-selected-chunk={isSelected ? "true" : undefined}
    >
      <div
        data-ixd-chunk-title
        className="label-2-medium text-label-normal mb-[0.75rem]"
      >
        {chunk.title}
      </div>

      <div className="flex flex-col gap-10">
        {chunk.dlViews.map((dlView, index) => (
          <ChunkDlViewItem
            key={`${chunk.id}-dl-${index}`}
            chunkId={chunk.id}
            renderScopeId={renderScopeId}
            sourceDlViewIndex={sourceDlViewIndexOffset + index}
            dlView={dlView}
            index={index}
            isLast={index === chunk.dlViews.length - 1}
            hideBeforePreviousPeriod={hideBeforePreviousPeriod}
            showPeriodLabel={showPeriodLabels}
            cellChanges={cellChanges}
            changeOrigins={changeOrigins}
            cellIdentityByNormalizedCellId={cellIdentityByNormalizedCellId}
            identityChanges={identityChanges}
            onCellEdit={onCellEdit}
          />
        ))}
      </div>
    </div>
  );
}, areChunkPropsEqual);

Chunk.displayName = "Chunk";
